// texe-web/src/components/FollowersModal.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, API_BASE_URL } from "../lib/api";
import Avatar from "./Avatar";

type FollowUser = {
  id: string;
  username: string;
  email?: string;
  bio?: string;
  avatarUrl?: string;
  avatarUrlSmall?: string;
};

type Props = {
  userId: string;
  type: "followers" | "following";
  onClose: () => void;
};

function absImg(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

export default function FollowersModal({ userId, type, onClose }: Props) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const endpoint = type === "followers" 
          ? `/api/follow/followers/${userId}`
          : `/api/follow/following/${userId}`;
        
        const res = await apiGet<{ ok: true; items: FollowUser[] }>(endpoint);
        if (!ignore) {
          setUsers(res.items);
        }
      } catch (error) {
        console.error("Failed to load users:", error);
        if (!ignore) setUsers([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [userId, type]);

  const handleUserClick = (user: FollowUser) => {
    navigate(`/profile/${user.id}`);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#1a1a1a",
          border: "2px solid #114232",
          borderRadius: 12,
          width: "100%",
          maxWidth: 500,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "2px solid #114232",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <h3 style={{
            margin: 0,
            color: "#114232",
            fontSize: 18,
            fontWeight: 700,
          }}>
            {type === "followers" ? "Takipçiler" : "Takip Edilenler"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#888",
              fontSize: 24,
              cursor: "pointer",
              padding: 0,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          overflowY: "auto",
          padding: "8px 0",
        }}>
          {loading ? (
            <div style={{
              padding: 40,
              textAlign: "center",
              color: "#888",
            }}>
              Yükleniyor...
            </div>
          ) : users.length === 0 ? (
            <div style={{
              padding: 40,
              textAlign: "center",
              color: "#666",
            }}>
              {type === "followers" ? "Henüz takipçi yok" : "Henüz kimseyi takip etmiyor"}
            </div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                onClick={() => handleUserClick(user)}
                style={{
                  padding: "12px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(17, 66, 50, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <Avatar 
                  src={absImg(user.avatarUrlSmall || user.avatarUrl)}
                  alt={user.username}
                  size={48}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    marginBottom: 2,
                  }}>
                    {user.username}
                  </div>
                  {user.bio && (
                    <div style={{
                      color: "#888",
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {user.bio}
                    </div>
                  )}
                </div>
                <div style={{
                  color: "#114232",
                  fontSize: 18,
                }}>
                  →
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
